import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Play, RotateCcw, BookOpen, Clock, Award, Medal } from 'lucide-react';
import { CircularProgress } from './CircularProgress';
import { ModuleChecklist } from './ModuleChecklist';
import { CourseImageCard } from '@/components/course/CourseImageCard';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
interface ModuleInfo {
  id: string;
  title: string;
  progress: number;
  hasQuiz?: boolean;
  quizPassed?: boolean;
}

type CourseStatus = 'not_started' | 'in_progress' | 'completed';

interface CourseProgressCardProps {
  courseId: string;
  title: string;
  category?: string;
  progress: number;
  completedModules: number;
  totalModules: number;
  modules?: ModuleInfo[];
  lastAccessedAt?: Date;
  quizzesPassed?: number;
  totalQuizzes?: number;
  currentModuleId?: string;
  status?: CourseStatus;
  onContinue: () => void;
  onModuleClick?: (moduleId: string) => void;
  className?: string;
}

const statusStyles: Record<CourseStatus, string> = {
  completed: 'border-green-500/50 bg-green-50/30 dark:bg-green-950/20',
  in_progress: 'border-blue-500/50 bg-blue-50/30 dark:bg-blue-950/20',
  not_started: 'border-muted bg-muted/10',
};

export const CourseProgressCard: React.FC<CourseProgressCardProps> = ({
  courseId,
  title,
  category,
  progress,
  completedModules,
  totalModules,
  modules = [],
  lastAccessedAt,
  quizzesPassed = 0,
  totalQuizzes = 0,
  currentModuleId,
  status = 'not_started',
  onContinue,
  onModuleClick,
  className,
}) => {
  const navigate = useNavigate();
  const isCompleted = status === 'completed';
  const isNotStarted = status === 'not_started';

  const getStatusConfig = () => {
    if (isCompleted) {
      return {
        badge: <Badge className="bg-green-500/90 text-white">Completed</Badge>,
        button: (
          <Button variant="outline" onClick={onContinue} className="gap-2">
            <RotateCcw className="h-4 w-4" />
            Review
          </Button>
        ),
      };
    }
    if (isNotStarted) {
      return {
        badge: <Badge variant="secondary">Not Started</Badge>,
        button: (
          <Button onClick={onContinue} className="gap-2">
            <Play className="h-4 w-4" />
            Start
          </Button>
        ),
      };
    }
    return {
      badge: <Badge className="bg-primary/90 text-primary-foreground">In Progress</Badge>,
      button: (
        <Button onClick={onContinue} className="gap-2">
          <Play className="h-4 w-4" />
          Continue
        </Button>
      ),
    };
  };

  const { badge, button } = getStatusConfig();

  return (
    <Card className={cn(
      'group transition-all duration-200 hover:shadow-lg border-2 overflow-hidden',
      statusStyles[status],
      className
    )}>
      {/* Course Thumbnail Header */}
      <div className="relative h-28">
        <CourseImageCard
          category={category}
          className="w-full h-full"
          showCategoryLabel={true}
        />
        {/* Progress overlay */}
        <div className="absolute top-2 right-2">
          <CircularProgress value={progress} size="sm" />
        </div>
      </div>
      
      <CardHeader className="pb-3 pt-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {badge}
            </div>
            <h3 className="font-semibold text-lg truncate group-hover:text-primary transition-colors">
              {title}
            </h3>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Stats */}
        <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <BookOpen className="h-4 w-4" />
            {completedModules}/{totalModules} modules
          </span>
          {totalQuizzes > 0 && (
            <span className="flex items-center gap-1">
              <Award className="h-4 w-4" />
              {quizzesPassed}/{totalQuizzes} quizzes
            </span>
          )}
          {lastAccessedAt && (
            <span className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              {formatDistanceToNow(lastAccessedAt, { addSuffix: true })}
            </span>
          )}
        </div>
        
        {/* Module Checklist (collapsible) */}
        {modules.length > 0 && (
          <ModuleChecklist
            modules={modules}
            currentModuleId={currentModuleId}
            onModuleClick={onModuleClick}
          />
        )}
        
        {/* Action Buttons */}
        <div className="pt-2 space-y-2">
          {button}
          
          {/* Certificate Link for Completed Courses */}
          {isCompleted && (
            <Button 
              variant="outline" 
              className="w-full gap-2 border-green-500/50 text-green-600 hover:bg-green-500/10 hover:text-green-700"
              onClick={() => navigate(`/certificates?course=${courseId}`)}
            >
              <Medal className="h-4 w-4" />
              View Certificate
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CourseProgressCard;
